/**
 * 63.14 step 4 (ARCHITECTURE §5.22 "destruction per module, and what it changes"; DECISIONS 201 (2) rifles do not hurt
 * concrete, (6) engineers clear, decay as the backstop): a modular building's modules take blows one by one. Each module
 * has its own health (constants/modules.ts MODULE_BODY: by kind, with a material for the damage table) in
 * `StructureState.modules` (absent = all whole; written on the first blow); a bullet wears the module its round struck
 * (the nearest to the hit point), a blast every module within its circle by its own distance (as 63.4 step 4's marked
 * parts). At 0 the module is destroyed: its shape leaves the walk, sight and the bullets (core/moduleShapes.ts omits it)
 * — a breach — and a wall-like module leaves **rubble** (`StructureState.rubble`): a low heap at its foot the walk steps
 * over and a round at ground height stops at, drawn in the kit's destroyed form, which **decays** after RUBBLE.decayTicks
 * (the clearing by engineers is a later step; decay first, as the owner ruled). The building's own health is as today
 * (its material's share of each blow; the ruin at 0): the modules are beside it, as the marked parts are. Pure.
 */
import type { GameState, PlanetState, StructureState, Vec3 } from '../types';
import type { BlowCause } from '../types/body';
import type { BuildingLayout } from '../types/modules';
import { COLLAPSE, MODULE_BODY, MODULE_SHAPES, RUBBLE, RUBBLE_KINDS } from '../constants/modules';
import { DAMAGE_VS } from '../constants/damage';
import { layoutFor, posesOf } from './modules';
import { structureFrame } from './hits';
import { getSurfaceRadius } from './surface';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecScale, vecNormalize, vecSub, vecDot } from '../math';

type Config = Pick<GameState, 'config'>['config'];

/** A module's health as it stands (its kind's full health while the structure carries none). */
export function moduleHealth(s: Pick<StructureState, 'modules'>, layout: BuildingLayout, k: number): number {
  return s.modules?.[k] ?? MODULE_BODY[layout.modules[k].kind].health;
}

/** The modules at 0: destroyed (their shapes gone). */
export function destroyedModules(s: Pick<StructureState, 'modules'>): ReadonlySet<number> {
  const out = new Set<number>();
  s.modules?.forEach((h, k) => { if (h <= 0) out.add(k); });
  return out;
}

/** cos and sin of a quarter turn (exact). */
const COS = [1, 0, -1, 0] as const, SIN = [0, 1, 0, -1] as const;

/**
 * How far `pt` (world) is from each module of a standing modular structure — the nearest of its parts' boxes, in people
 * (Infinity for a destroyed module, or one with no parts). Null when the structure has no layout here.
 */
export function moduleDistances(pt: Vec3, s: StructureState, state: Pick<GameState, 'planets' | 'config'>): number[] | null {
  const layout = layoutFor(s, state.config), planet = state.planets[s.planetIndex];
  if (!layout || !planet || s.health <= 0) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
  const base = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));
  const r = vecSub(pt, base);
  const px = vecDot(r, tx) / person, py = vecDot(r, up) / person, pz = vecDot(r, tz) / person;   // the point in the building's frame (people)
  const poses = posesOf(layout);
  return layout.modules.map((m, k) => {
    if (moduleHealth(s, layout, k) <= 0) return Infinity;
    const parts = MODULE_SHAPES[m.kind];
    if (parts.length === 0) return Infinity;
    const pose = poses[k], turn = m.turn ?? 0, c = COS[turn], sn = SIN[turn];
    // the point in the module's own frame (undo the pose: move, then turn back)
    const lx = px - pose.x, ly = py - pose.y, lz = pz - pose.z;
    const mx = lx * c - lz * sn, mz = lx * sn + lz * c;
    let best = Infinity;
    for (const p of parts) {
      if (p.leaf) continue;
      const dx = Math.max(0, Math.abs(mx - p.centre[0]) - p.half[0]), dy = Math.max(0, Math.abs(ly - p.centre[1]) - p.half[1]), dz = Math.max(0, Math.abs(mz - p.centre[2]) - p.half[2]);
      best = Math.min(best, Math.sqrt(dx * dx + dy * dy + dz * dz));
    }
    return best;
  });
}

/** A structure with module `k` worn by `amount` of `cause` (the table's share for the module's material); at 0 its rubble filed at `tick`. The same structure when nothing changes. */
function wearModule(s: StructureState, layout: BuildingLayout, k: number, amount: number, cause: BlowCause, tick: number): StructureState {
  const kind = layout.modules[k].kind, body = MODULE_BODY[kind];
  const blow = amount * DAMAGE_VS[cause][body.material];
  const before = moduleHealth(s, layout, k);
  if (!(blow > 0) || before <= 0) return s;
  const after = Math.max(0, before - blow);
  const modules = s.modules ? s.modules.slice() : layout.modules.map((m) => MODULE_BODY[m.kind].health);
  modules[k] = after;
  const next: StructureState = { ...s, modules };
  if (after <= 0 && RUBBLE_KINDS.has(kind)) next.rubble = [...(s.rubble ?? []), { k, at: tick }];
  return next;
}

/**
 * 72.3 (ARCHITECTURE §5.35 (b), the foundation; a building whose health is its modules', GameConfig.collapse): the ground
 * dropped under a modular building. The building sits on the ground at its middle, so it settles with that; each standing
 * ground-floor module (the plinth aside) whose foot the ground fell under by more than COLLAPSE.underminePeople **past the
 * building's own settling** loses its bearing — destroyed, whatever its material, its rubble filed at `tick` — and the
 * collapse's solve does the rest. `even` (the old whole-building drop's blow, when its settling passed that rule's threshold)
 * wears every other standing ground-floor module through the table's `drop` row. A foot is read at its module's middle.
 * The same structure when nothing changed.
 */
export function undermineModules(s: StructureState, layout: BuildingLayout, oldPlanet: PlanetState, newPlanet: PlanetState, state: Pick<GameState, 'config'>, tick: number, even: number): StructureState {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = oldPlanet.radius;
  const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up), poses = posesOf(layout);
  const settled = getSurfaceRadius(oldPlanet, up) - getSurfaceRadius(newPlanet, up);
  let next = s;
  layout.modules.forEach((m, k) => {
    if ((m.floor ?? 0) !== 0 || m.kind === 'plinth' || moduleHealth(next, layout, k) <= 0) return;
    const p = poses[k], foot = vecNormalize(vecAdd(up, vecAdd(vecScale(tx, (p.x * person) / R), vecScale(tz, (p.z * person) / R))));
    if (getSurfaceRadius(oldPlanet, foot) - getSurfaceRadius(newPlanet, foot) - settled > COLLAPSE.underminePeople * person) {
      const modules = next.modules ? next.modules.slice() : layout.modules.map(x => MODULE_BODY[x.kind].health);
      modules[k] = 0;
      next = { ...next, modules, ...(RUBBLE_KINDS.has(m.kind) ? { rubble: [...(next.rubble ?? []), { k, at: tick }] } : {}) };
    } else if (even > 0) next = wearModule(next, layout, k, even, 'drop', tick);
  });
  return next;
}

/** The match's clock for the rubble: the action tick (the turn number on the turn clock). */
const tickOf = (state: Pick<GameState, 'tick' | 'turnNumber'>): number => state.tick ?? state.turnNumber;

/**
 * A round into a modular building: the module nearest the hit point takes the blow. The state with that structure
 * replaced (or the same state: no layout, nothing hit, or the module takes nothing of that cause).
 */
export function moduleBlow(state: GameState, structureId: number, point: Vec3, amount: number, cause: BlowCause): GameState {
  const s = state.structures.find(x => x.id === structureId);
  if (!s || s.health <= 0) return state;
  const layout = layoutFor(s, state.config);
  if (!layout) return state;
  const dists = moduleDistances(point, s, state);
  if (!dists) return state;
  let k = -1, best = Infinity;
  dists.forEach((d, i) => { if (d < best) { best = d; k = i; } });
  if (k < 0 || !(best <= RUBBLE.hitReachPeople)) return state;
  const next = wearModule(s, layout, k, amount, cause, tickOf(state));
  return next === s ? state : { ...state, structures: state.structures.map(x => (x.id === s.id ? next : x)) };
}

/**
 * A blast on a modular building's modules: each within reach takes `dmgAt(dist)` (the blast's damage at that distance,
 * the side's share in) through the table for its material, by its own distance (`dists`, from moduleDistances). The
 * structure with its modules worn, or the same one.
 */
export function blastModules(s: StructureState, layout: BuildingLayout, dists: readonly number[], dmgAt: (dist: number) => number, cause: BlowCause, tick: number): StructureState {
  let next = s;
  for (let k = 0; k < layout.modules.length; k++) {
    const d = dists[k] ?? Infinity;
    if (!Number.isFinite(d)) continue;
    const amount = dmgAt(d);
    if (!(amount > 0)) continue;
    next = wearModule(next, layout, k, amount, cause, tick);
  }
  return next;
}

/**
 * The rubble's decay (201 (6): decay as the backstop for performance; the engineers' clearing is a later step): a heap
 * older than RUBBLE.decayTicks is gone — its shape leaves the walk and the bullets, its drawn form too. One new structure
 * object per structure whose rubble changed; the same state when none did.
 */
export function stepRubble(state: GameState): GameState {
  const tick = tickOf(state);
  let structures: StructureState[] | null = null;
  state.structures.forEach((s, i) => {
    if (!s.rubble?.length) return;
    const kept = s.rubble.filter(r => tick - r.at < RUBBLE.decayTicks);
    if (kept.length === s.rubble.length) return;
    (structures ??= state.structures.slice())[i] = kept.length ? { ...s, rubble: kept } : (({ rubble: _r, ...rest }) => rest)(s);
  });
  return structures ? { ...state, structures } : state;
}

/** Whether `config` plays modules as bodies here (the switch, and the buildings sized by function). */
export function modulesTakeBlows(config: Config): boolean {
  return !!config.modularBuildings;
}
